import { useState } from 'react'

import { Text } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'

import QuestionTemplate from './QuestionTemplate'

export interface QuestionTemplate {
  questionTemplateId?: string
  question: string
  variableName: string
  shortQuestionName?: string
  variableType: 'STRING' | 'NUMBER' | 'BOOLEAN'
  condition?: string
  disqualifiedReason?: string
  status?: string
  createdAt?: string
  updatedAt?: string
}

interface QuestionTemplatesSectionProps {
  questionTemplates: QuestionTemplate[]
  onQuestionTemplatesChange: (questionTemplates: QuestionTemplate[]) => void
  disabled?: boolean
  /**
   * Whether technical fields (Variable Name, Answer Type, Condition) are
   * shown. Hidden from clients — these key answer mapping and branching.
   */
  showTechnicalFields?: boolean
  /** Whether questions can be added/removed (admin-only for clients). */
  canModifyQuestions?: boolean
}

const QuestionTemplatesSection = ({
  questionTemplates,
  onQuestionTemplatesChange,
  disabled = false,
  showTechnicalFields = true,
  canModifyQuestions = true,
}: QuestionTemplatesSectionProps) => {
  const [localQuestionTemplates, setLocalQuestionTemplates] = useState<
    QuestionTemplate[]
  >(questionTemplates.length > 0 ? questionTemplates : [])

  const updateQuestionTemplate = (
    index: number,
    field: keyof QuestionTemplate,
    value: any
  ) => {
    const updated = [...localQuestionTemplates]
    updated[index] = { ...updated[index], [field]: value }
    setLocalQuestionTemplates(updated)
    onQuestionTemplatesChange(updated)
  }

  const addQuestionTemplate = () => {
    const newQuestionTemplate: QuestionTemplate = {
      question: '',
      variableName: '',
      shortQuestionName: '',
      variableType: 'STRING',
    }
    const updated = [...localQuestionTemplates, newQuestionTemplate]
    setLocalQuestionTemplates(updated)
    onQuestionTemplatesChange(updated)
  }

  const removeQuestionTemplate = (index: number) => {
    const updated = localQuestionTemplates.filter((_, i) => i !== index)
    setLocalQuestionTemplates(updated)
    onQuestionTemplatesChange(updated)
  }

  return (
    <div className="flex flex-col gap-4">
      {localQuestionTemplates.length > 0 && (
        <div className="mb-4 space-y-4">
          {localQuestionTemplates.map((questionTemplate, index) => (
            <QuestionTemplate
              key={index}
              questionTemplate={questionTemplate}
              index={index}
              removeQuestionTemplate={removeQuestionTemplate}
              disabled={disabled}
              updateQuestionTemplate={updateQuestionTemplate}
              showTechnicalFields={showTechnicalFields}
              canModifyQuestions={canModifyQuestions}
            />
          ))}
        </div>
      )}
      {localQuestionTemplates.length === 0 && (
        <Text size="xs">No questions configured.</Text>
      )}

      {canModifyQuestions && (
        <div className="flex flex-row items-center gap-2">
          <DNButton
            variant="secondary"
            onClick={addQuestionTemplate}
            disabled={disabled}
          >
            Add Question
          </DNButton>
        </div>
      )}
    </div>
  )
}

export default QuestionTemplatesSection
